// IQ Test — pure IQ calculator, the one production IQ calculation path
// (fixed Mean=40.5/SD=7.8 raw-score norm). Framework-independent: no
// React, no reads from reportData. report-model.js is the only caller.

import { IQ_SCORING_CONFIG, PSYCHOMETRIC_CLASSIFICATION_BANDS } from "./iq-psychometric-config";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Standard normal CDF — Abramowitz & Stegun 26.2.17 (|error| < 7.5e-8).
// Used only to derive `percentile` below.
export function normCdf(z) {
  const b = [0.31938153, -0.356563782, 1.781477937, -1.821255978, 1.330274429];
  const x = Math.abs(z);
  const t = 1 / (1 + 0.2316419 * x);
  const poly = t * (b[0] + t * (b[1] + t * (b[2] + t * (b[3] + t * b[4]))));
  const tail = 0.39894228 * Math.exp((-x * x) / 2) * poly;
  return z >= 0 ? 1 - tail : tail;
}

// 7-band classification for the DEVIATION/IQ INDEX only — distinct from
// the 5-band PERCENTAGE performance classification
// (deriveBandKeyFromPercentage in iq-calculations.js). Never confuse the
// two.
export function classifyIndex(index) {
  const band = PSYCHOMETRIC_CLASSIFICATION_BANDS.find((b) => index >= b.min && index <= b.max);
  return band ? { key: band.key, label: band.label } : null;
}

// The one production IQ calculation:
//   z = (overallRawScore - rawMean) / rawSd
//   estimatedIq = clamp(round(iqMean + iqSd * z), iqFloor, iqCeil)
// Must only ever be called with the OVERALL raw score/config — there is
// no per-section IQ. Returns null for a non-numeric rawScore, never a
// fabricated result. `classification` and `percentile` both derive from
// this same zScore — no separate/second formula. confidenceInterval,
// reliability, SEM, and scaledScore are deliberately absent: no confirmed
// methodology exists for them under this model.
export function computeFixedNormIq(rawScore, config = IQ_SCORING_CONFIG) {
  if (rawScore == null || !Number.isFinite(rawScore)) return null;

  const zScore = (rawScore - config.rawMean) / config.rawSd;
  const unroundedIq = config.iqMean + config.iqSd * zScore;
  const roundedIq = Math.round(unroundedIq);
  const estimatedIq = clamp(roundedIq, config.iqFloor, config.iqCeil);
  const percentileExact = normCdf(zScore) * 100;

  return {
    rawScore,
    maxRawScore: config.items,
    zScore,
    estimatedIq,
    classification: classifyIndex(estimatedIq),
    percentile: Math.round(percentileExact),
  };
}
